/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import '../api/axiosConfig';
import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Card } from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import useAudioToast from '../hooks/useAudioToast';

const hasVehicleData = (vehicle) => {
  if (!vehicle || typeof vehicle !== 'object') return false;
  return Object.values(vehicle).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });
};

export default function UpdateAccount() {
  const audioToast = useAudioToast();
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);
  const [licensingBodies, setLicensingBodies] = useState([]);

  const defaultSpareVehicle = {
    carMake: '',
    carModel: '',
    carSeats: '',
    regNumber: '',
    vehicleLicenseId: '',
    vehicleLicenseExpiry: '',
    roadTaxExpiry: '',
    motExpiry: '',
    insuranceExpiry: '',
    vehicleInspectionExpiry: '',
    photoOfCar: ''
  };

  const normalizeDateValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }
    return value;
  };

  const normalizeSpareVehicle = (data = {}) => {
    const normalized = { ...defaultSpareVehicle };
    const source = data && typeof data === 'object' ? data : {};
    Object.keys(defaultSpareVehicle).forEach((key) => {
      normalized[key] = source[key] ?? defaultSpareVehicle[key];
    });
    return normalized;
  };

  const formatDateDisplay = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
  };

  // ── Load profile on mount ──
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/user/profile');
        setUser(res.data);
        setForm({
          name: res.data.name || '',
          companyName: res.data.companyName || '',
          companyAddress: res.data.companyAddress || '',
          carMake: res.data.carMake || '',
          carModel: res.data.carModel || '',
          carSeats: res.data.carSeats || '',
          regNumber: res.data.regNumber || '',
          email: res.data.email || '',
          phoneNumber: res.data.phoneNumber || '',
          website: res.data.website || '',
          licensingAuthority: res.data.licensingAuthority || '',
          badgeNumber: res.data.badgeNumber || '',
          licenseType: res.data.licenseType || 'Hackney License',
          logo: res.data.logo || '',
          photoOfCar: res.data.photoOfCar || '',
          photoOfDriver: res.data.photoOfDriver || '',
          mainPostcode: res.data.mainPostcode || '',
          postcodeSectorsCovered: res.data.postcodeSectorsCovered || [],
          accountType: res.data.accountType || 'Personal',
          accountName: res.data.accountName || '',
          accountSortCode: res.data.accountSortCode || '',
          accountNumber: res.data.accountNumber || '',
          paymentMethods: res.data.paymentMethods || ['Cash'],
          leadEmailOptIn: Boolean(res.data.leadEmailOptIn),
          emailDiaryDaily: Boolean(res.data.emailDiaryDaily),
          dispatchAccessAccepted: Boolean(res.data.dispatchAccessAccepted),
          dispatchEmail: res.data.dispatchEmail || '',
          audioNotifications: Boolean(res.data.audioNotifications !== false), // Default to true if not set
          webLinksToDiary: Boolean(res.data.webLinksToDiary),
          preferredVehicleForJobs: res.data.preferredVehicleForJobs || 'primary',
          roadTaxExpiry: res.data.roadTaxExpiry || '',
          motExpiry: res.data.motExpiry || '',
          insuranceExpiry: res.data.insuranceExpiry || '',
          vehicleInspectionExpiry: res.data.vehicleInspectionExpiry || '',
          vehicleLicenseId: res.data.vehicleLicenseId || '',
          vehicleLicenseExpiry: res.data.vehicleLicenseExpiry || '',
          spareVehicle: normalizeSpareVehicle(res.data.spareVehicle || {})
        });

        // Load profile status
        const statusRes = await axios.get('/auth/profile-status');
        setProfileStatus(statusRes.data);
      } catch (err) {
        console.error(err);
        audioToast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    })();

    // Fetch licensing bodies for dropdown
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/licensing-bodies', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter only active licensing bodies
        const activeBodies = response.data.filter(lb => lb.active !== false);
        setLicensingBodies(activeBodies);
      } catch (err) {
        console.error('Error fetching licensing bodies:', err);
        // Non-fatal error, continue without dropdown
      }
    })();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const handlePaymentMethodChange = (method) => {
    const currentMethods = form.paymentMethods || [];
    const updatedMethods = currentMethods.includes(method)
      ? currentMethods.filter(m => m !== method)
      : [...currentMethods, method];
    setForm({ ...form, paymentMethods: updatedMethods });
  };

  const handleSpareVehicleChange = (name, value) => {
    setForm(prev => ({
      ...prev,
      spareVehicle: {
        ...(prev.spareVehicle ? { ...prev.spareVehicle } : { ...defaultSpareVehicle }),
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const {
        name, carMake, carModel, carSeats, regNumber,
        licensingAuthority, badgeNumber, licenseType,
        companyName, phoneNumber, website, logo, photoOfCar, photoOfDriver,
        mainPostcode, postcodeSectorsCovered, accountType, accountName, accountSortCode, accountNumber
      } = form;

      // 1️⃣ Update existing schema fields via PUT
      await axios.put('/user/profile', { companyName, companyAddress: form.companyAddress, phoneNumber, website, leadEmailOptIn: !!form.leadEmailOptIn, emailDiaryDaily: !!form.emailDiaryDaily, dispatchAccessAccepted: !!form.dispatchAccessAccepted, dispatchEmail: form.dispatchEmail, audioNotifications: !!form.audioNotifications, webLinksToDiary: !!form.webLinksToDiary });

      // 2️⃣ Update extended fields via PATCH
      const spareVehiclePayload = {
        carMake: form.spareVehicle?.carMake || '',
        carModel: form.spareVehicle?.carModel || '',
        carSeats: form.spareVehicle?.carSeats || '',
        regNumber: form.spareVehicle?.regNumber || '',
        vehicleLicenseId: form.spareVehicle?.vehicleLicenseId || '',
        vehicleLicenseExpiry: normalizeDateValue(form.spareVehicle?.vehicleLicenseExpiry),
        roadTaxExpiry: normalizeDateValue(form.spareVehicle?.roadTaxExpiry),
        motExpiry: normalizeDateValue(form.spareVehicle?.motExpiry),
        insuranceExpiry: normalizeDateValue(form.spareVehicle?.insuranceExpiry),
        vehicleInspectionExpiry: normalizeDateValue(form.spareVehicle?.vehicleInspectionExpiry),
        photoOfCar: form.spareVehicle?.photoOfCar || ''
      };

      const patchData = {
         name, carMake, carModel, carSeats, regNumber,
         licensingAuthority, badgeNumber, licenseType, logo, photoOfCar, photoOfDriver,
         mainPostcode, postcodeSectorsCovered, accountType, accountName, accountSortCode, accountNumber,
         paymentMethods: form.paymentMethods,
         leadEmailOptIn: !!form.leadEmailOptIn, emailDiaryDaily: !!form.emailDiaryDaily, dispatchAccessAccepted: !!form.dispatchAccessAccepted, audioNotifications: !!form.audioNotifications, webLinksToDiary: !!form.webLinksToDiary,
         roadTaxExpiry: normalizeDateValue(form.roadTaxExpiry),
         motExpiry: normalizeDateValue(form.motExpiry),
         insuranceExpiry: normalizeDateValue(form.insuranceExpiry),
         vehicleInspectionExpiry: normalizeDateValue(form.vehicleInspectionExpiry),
         vehicleLicenseId: form.vehicleLicenseId || '',
         vehicleLicenseExpiry: normalizeDateValue(form.vehicleLicenseExpiry),
         preferredVehicleForJobs: form.preferredVehicleForJobs || 'primary',
         spareVehicle: spareVehiclePayload
      };
      if (formSpareVehicleHasData) {
        patchData.spareVehicle = spareVehiclePayload;
        patchData.preferredVehicleForJobs = form.preferredVehicleForJobs === 'spare' ? 'spare' : 'primary';
      } else {
        patchData.spareVehicle = null;
        patchData.preferredVehicleForJobs = 'primary';
      }

      await axios.patch('/user/profile/details', patchData);

      // 3️⃣ Mark profile as completed if basic info is filled
      if (name && phoneNumber && companyName) {
        try {
          const completeResponse = await axios.post('/user/profile/complete');
          if (completeResponse.data.recommendedFields) {
            audioToast.warning(`Profile completed! Consider adding: ${completeResponse.data.recommendedFields.join(', ')}`);
          }
        } catch (completeError) {
          if (completeError.response?.data?.missingFields) {
            audioToast.error(`Missing required fields: ${completeError.response.data.missingFields.join(', ')}`);
          } else {
            audioToast.error('Failed to complete profile');
          }
          throw completeError;
        }
      }

      // 4️⃣ Refresh local data
      const res = await axios.get('/user/profile');
      setUser(res.data);
      
      // 5️⃣ Update form data to reflect changes
      setForm({
        name: res.data.name || '',
        companyName: res.data.companyName || '',
        companyAddress: res.data.companyAddress || '',
        carMake: res.data.carMake || '',
        carModel: res.data.carModel || '',
        carSeats: res.data.carSeats || '',
        regNumber: res.data.regNumber || '',
        email: res.data.email || '',
        phoneNumber: res.data.phoneNumber || '',
        website: res.data.website || '',
        licensingAuthority: res.data.licensingAuthority || '',
        badgeNumber: res.data.badgeNumber || '',
        licenseType: res.data.licenseType || 'Hackney License',
        logo: res.data.logo || '',
        photoOfCar: res.data.photoOfCar || '',
        photoOfDriver: res.data.photoOfDriver || '',
        mainPostcode: res.data.mainPostcode || '',
        postcodeSectorsCovered: res.data.postcodeSectorsCovered || [],
        accountType: res.data.accountType || 'Personal',
        accountName: res.data.accountName || '',
        accountSortCode: res.data.accountSortCode || '',
        accountNumber: res.data.accountNumber || '',
        paymentMethods: res.data.paymentMethods || ['Cash'],
        leadEmailOptIn: Boolean(res.data.leadEmailOptIn),
        emailDiaryDaily: Boolean(res.data.emailDiaryDaily),
        dispatchAccessAccepted: Boolean(res.data.dispatchAccessAccepted),
        dispatchEmail: res.data.dispatchEmail || '',
        audioNotifications: Boolean(res.data.audioNotifications !== false), // Default to true if not set
        webLinksToDiary: Boolean(res.data.webLinksToDiary),
        preferredVehicleForJobs: res.data.preferredVehicleForJobs || 'primary',
        roadTaxExpiry: res.data.roadTaxExpiry || '',
        motExpiry: res.data.motExpiry || '',
        insuranceExpiry: res.data.insuranceExpiry || '',
        vehicleInspectionExpiry: res.data.vehicleInspectionExpiry || '',
        vehicleLicenseId: res.data.vehicleLicenseId || '',
        vehicleLicenseExpiry: res.data.vehicleLicenseExpiry || '',
        spareVehicle: normalizeSpareVehicle(res.data.spareVehicle || {})
      });
      
      // 6️⃣ Refresh profile status
      const statusRes = await axios.get('/auth/profile-status');
      setProfileStatus(statusRes.data);
      
      toast.success('Details updated successfully');
      setShowModal(false);
    } catch (err) {
      console.error(err);
      toast.error('Update failed');
    }
  };

  const handleLicensingSubmission = async () => {
    try {
      const { licensingAuthority, badgeNumber, licenseType, regNumber } = form;
      
      if (!licensingAuthority || !badgeNumber || !licenseType || !regNumber) {
        toast.error('Please complete all licensing fields before submitting');
        return;
      }

      await axios.post('/user/licensing/submit');
      
      // Refresh profile status
      const statusRes = await axios.get('/auth/profile-status');
      setProfileStatus(statusRes.data);
      
      toast.success('Licensing information submitted for review');
    } catch (err) {
      console.error(err);
      if (err.response?.data?.missingFields) {
        toast.error(`Missing licensing fields: ${err.response.data.missingFields.join(', ')}`);
      } else if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Failed to submit licensing information');
      }
    }
  };

  const hasSpareVehicle = hasVehicleData(user?.spareVehicle);
  const formSpareVehicleHasData = hasVehicleData(form.spareVehicle);

  useEffect(() => {
    if (!formSpareVehicleHasData && form.preferredVehicleForJobs === 'spare') {
      setForm((prev) => ({ ...prev, preferredVehicleForJobs: 'primary' }));
    }
  }, [formSpareVehicleHasData, form.preferredVehicleForJobs]);

  if (loading) return <div className="text-center py-5">Loading...</div>;

  return (
    <>
      <ToastContainer position="top-center" />
      <div className="container mt-4">
        {/* Sticky Header - Page Content Only */}
        <div className="sticky-top bg-white border-bottom pb-3" style={{ 
          zIndex: 1020,
          top: '56px' // Position below the main navbar
        }}>
          <div className="container">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h4 className="mb-0">Your Profile</h4>
              </div>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <i className="bi bi-pencil-square me-2"></i>Update Details
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Card className="mb-3">
          <Card.Body className="bg-light p-4 rounded shadow-sm">

          {/* Security Status Summary */}
          {profileStatus && (
            <div className="mb-4 p-3 border rounded">
              <h6 className="text-primary mb-3">
                <i className="bi bi-shield-check me-2"></i>Account Security Status
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-2">
                    <span className={`badge ${profileStatus.profileCompleted ? 'bg-success' : 'bg-danger'} me-2`}>
                      {profileStatus.profileCompleted ? '✓' : '✗'}
                    </span>
                    <span>Profile Complete</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className={`badge ${profileStatus.licensingSubmitted ? 'bg-success' : 'bg-warning'} me-2`}>
                      {profileStatus.licensingSubmitted ? '✓' : '!'}
                    </span>
                    <span>Licensing Submitted</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className={`badge ${profileStatus.licensingApproved ? 'bg-success' : 'bg-warning'} me-2`}>
                      {profileStatus.licensingApproved ? '✓' : '!'}
                    </span>
                    <span>Licensing Approved</span>
                  </div>
                </div>
                <div className="col-md-6">
                  {!profileStatus.canAcceptJobs && (
                    <div className="alert alert-warning mb-0">
                      <strong>Action Required:</strong>
                      <ul className="mb-0 mt-2">
                        {!profileStatus.profileCompleted && (
                          <li>Complete your profile (Name, Phone, Company)</li>
                        )}
                        {!profileStatus.licensingSubmitted && (
                          <li>Submit licensing information</li>
                        )}
                        {!profileStatus.licensingApproved && profileStatus.licensingSubmitted && (
                          <li>Wait for licensing approval</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {profileStatus.canAcceptJobs && (
                    <div className="alert alert-success mb-0">
                      <strong>✓ All requirements met!</strong><br />
                      You can now accept job leads.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

  {/* Personal Information */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-person-circle me-2"></i>Personal Information
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Name</strong>
        <div className={`${!user.name ? 'text-danger fw-bold' : ''}`}>
          {user.name || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Email</strong>
        {user.email || '-'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Phone</strong>
        <div className={`${!user.phoneNumber ? 'text-danger fw-bold' : ''}`}>
          {user.phoneNumber || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Website</strong>
        {user.website ? (
          <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
            {user.website}
          </a>
        ) : '-'}
      </div>
    </div>
  </div>

  {/* Company Information */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-building me-2"></i>Company Information
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Company Name</strong>
        <div className={`${!user.companyName ? 'text-danger fw-bold' : ''}`}>
          {user.companyName || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Company Address</strong>
        {user.companyAddress || '-'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Main Postcode</strong>
        {user.mainPostcode || '-'}
      </div>
      <div className="col-md-12 mb-2">
        <strong className="d-block text-muted">Postcode Sectors Covered</strong>
        {user.postcodeSectorsCovered && user.postcodeSectorsCovered.length > 0 
          ? user.postcodeSectorsCovered.join(' ') 
          : '-'}
      </div>
    </div>
  </div>

  {/* Email Preferences */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-envelope-check me-2"></i>Email Preferences
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Receive new job leads by email</strong>
        {user.leadEmailOptIn ? 'Enabled' : 'Disabled'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Email me my Diary each morning at 6am</strong>
        {user.emailDiaryDaily ? 'Enabled' : 'Disabled'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Audio notifications for new jobs and alerts</strong>
        {user.audioNotifications !== false ? 'Enabled' : 'Disabled'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Insert WebLink jobs directly into My Diary</strong>
        {user.webLinksToDiary ? 'Enabled' : 'Disabled'}
      </div>
    </div>
  </div>

  {/* Dispatch Information */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      🚛 Dispatch Information
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Dispatch Module Access</strong>
        {user.dispatchAccessAccepted ? (
          <span className="text-success">✅ Enabled</span>
        ) : (
          <span className="text-muted">❌ Disabled</span>
        )}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Dispatcher Email</strong>
        {user.dispatchEmail ? (
          <a href={`mailto:${user.dispatchEmail}`} className="text-decoration-none">
            📧 {user.dispatchEmail}
          </a>
        ) : (
          <span className="text-muted">Not configured</span>
        )}
      </div>
    </div>
    {user.dispatchAccessAccepted && (
      <div className="mt-2">
        <small className="text-muted">
          <strong>Note:</strong> You have enabled dispatch module access. Dispatchers can view your diary and assign jobs to you directly.
        </small>
      </div>
    )}
  </div>

  {/* Vehicle Information */}
  <div className="mb-4">
    <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
      <h6 className="text-primary mb-0">
        <i className="bi bi-car-front me-2"></i>Vehicle Information
      </h6>
      {!hasSpareVehicle && (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setForm((prev) => ({
              ...prev,
              spareVehicle: prev.spareVehicle || { ...defaultSpareVehicle }
            }));
            setShowModal(true);
          }}
        >
          <i className="bi bi-plus-lg me-1"></i>Car No2
        </Button>
      )}
    </div>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Car Details</strong>
        {user.carMake || '-'} {user.carModel || ''} ({user.carSeats || '-'} seats)
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Registration</strong>
        <div className={`${!user.regNumber ? 'text-danger fw-bold' : ''}`}>
          {user.regNumber || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
    </div>
    <div className="row mt-2">
      <div className="col-md-6 mb-2">
        <small className="text-muted">Vehicle License ID:</small>
        <div>{user.vehicleLicenseId || '-'}</div>
      </div>
      <div className="col-md-6 mb-2">
        <small className="text-muted">Vehicle License Expiry:</small>
        <div>{formatDateDisplay(user.vehicleLicenseExpiry)}</div>
      </div>
    </div>
    <div className="row mt-3">
      <div className="col-12 mb-2">
        <strong className="d-block text-muted">📋 Document Expiry Dates</strong>
      </div>
      <div className="col-md-6 mb-2">
        <small className="text-muted">Road Tax Expiry:</small>
        <div>{user.roadTaxExpiry ? new Date(user.roadTaxExpiry).toLocaleDateString('en-GB') : '-'}</div>
      </div>
      <div className="col-md-6 mb-2">
        <small className="text-muted">MOT Expiry:</small>
        <div>{user.motExpiry ? new Date(user.motExpiry).toLocaleDateString('en-GB') : '-'}</div>
      </div>
      <div className="col-md-6 mb-2">
        <small className="text-muted">Insurance Expiry:</small>
        <div>{user.insuranceExpiry ? new Date(user.insuranceExpiry).toLocaleDateString('en-GB') : '-'}</div>
      </div>
      <div className="col-md-6 mb-2">
        <small className="text-muted">Vehicle Inspection Expiry:</small>
        <div>{user.vehicleInspectionExpiry ? new Date(user.vehicleInspectionExpiry).toLocaleDateString('en-GB') : '-'}</div>
      </div>
    </div>
    <div className="row mt-2">
      <div className="col-12 mb-2">
        <strong className="d-block text-muted">Job Card Vehicle</strong>
        {user?.preferredVehicleForJobs === 'spare' && hasSpareVehicle
          ? 'Car No2 (Spare Vehicle)'
          : 'Car No1 (Primary Vehicle)'}
      </div>
    </div>
    {hasSpareVehicle && (
      <div className="mt-4">
        <h6 className="text-primary border-bottom pb-2 mb-3">🚗 Spare / Second Vehicle</h6>
        <div className="row">
          <div className="col-md-6 mb-2">
            <strong className="d-block text-muted">Car Details</strong>
            {(user.spareVehicle?.carMake || '-')}{' '}
            {user.spareVehicle?.carModel || ''}{' '}
            {user.spareVehicle?.carSeats ? `(${user.spareVehicle.carSeats} seats)` : ''}
          </div>
          <div className="col-md-6 mb-2">
            <strong className="d-block text-muted">Registration</strong>
            {user.spareVehicle?.regNumber || '-'}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-6 mb-2">
            <small className="text-muted">Vehicle License ID:</small>
            <div>{user.spareVehicle?.vehicleLicenseId || '-'}</div>
          </div>
          <div className="col-md-6 mb-2">
            <small className="text-muted">Vehicle License Expiry:</small>
            <div>{formatDateDisplay(user.spareVehicle?.vehicleLicenseExpiry)}</div>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12 mb-2">
            <strong className="d-block text-muted">📋 Spare Vehicle Document Expiry Dates</strong>
          </div>
          <div className="col-md-6 mb-2">
            <small className="text-muted">Road Tax Expiry:</small>
            <div>{formatDateDisplay(user.spareVehicle?.roadTaxExpiry)}</div>
          </div>
          <div className="col-md-6 mb-2">
            <small className="text-muted">MOT Expiry:</small>
            <div>{formatDateDisplay(user.spareVehicle?.motExpiry)}</div>
          </div>
          <div className="col-md-6 mb-2">
            <small className="text-muted">Insurance Expiry:</small>
            <div>{formatDateDisplay(user.spareVehicle?.insuranceExpiry)}</div>
          </div>
          <div className="col-md-6 mb-2">
            <small className="text-muted">Vehicle Inspection Expiry:</small>
            <div>{formatDateDisplay(user.spareVehicle?.vehicleInspectionExpiry)}</div>
          </div>
        </div>
        {user.spareVehicle?.photoOfCar && (
          <div className="mt-2">
            <small className="text-muted d-block">Photo / Reference:</small>
            <a href={user.spareVehicle.photoOfCar} target="_blank" rel="noopener noreferrer">
              {user.spareVehicle.photoOfCar}
            </a>
          </div>
        )}
      </div>
    )}
  </div>

  {/* Licensing Information */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-card-text me-2"></i>Licensing Information
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">License Type</strong>
        <div className={`${!user.licenseType ? 'text-danger fw-bold' : ''}`}>
          {user.licenseType || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Licensing Authority</strong>
        <div className={`${!user.licensingAuthority ? 'text-danger fw-bold' : ''}`}>
          {user.licensingAuthority || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Badge Number</strong>
        <div className={`${!user.badgeNumber ? 'text-danger fw-bold' : ''}`}>
          {user.badgeNumber || <span className="text-danger">⚠️ REQUIRED - Missing</span>}
        </div>
      </div>
    </div>
  </div>

  {/* Visual Assets */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-images me-2"></i>Visual Assets
    </h6>
    <div className="row">
      <div className="col-md-4 mb-3">
        <strong className="d-block text-muted mb-2">Logo</strong>
        {(user.logo || form.logo) ? (
          <div className="position-relative d-inline-block">
            <img 
              src={form.logo || user.logo} 
              alt="Company Logo" 
              style={{ 
                maxHeight: '120px', 
                maxWidth: '200px', 
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: '#f8f9fa'
              }} 
            />
            <Button 
              variant="danger" 
              size="sm" 
              className="position-absolute top-0 end-0"
              style={{ 
                transform: 'translate(25%, -25%)',
                zIndex: 10,
                minWidth: '24px',
                width: '24px',
                height: '24px',
                padding: '0',
                fontSize: '12px'
              }}
              onClick={() => {
                setForm({ ...form, logo: '' });
                toast.success('Logo removed');
              }}
              title="Delete logo"
            >
              ×
            </Button>
          </div>
        ) : '-'}
      </div>
      <div className="col-md-4 mb-3">
        <strong className="d-block text-muted mb-2">Car Photo</strong>
        {(user.photoOfCar || form.photoOfCar) ? (
          <div className="position-relative d-inline-block">
              <img 
              src={form.photoOfCar || user.photoOfCar} 
              alt="Car" 
              style={{ 
                maxHeight: '120px', 
                maxWidth: '200px', 
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: '#f8f9fa'
              }} 
            />
            <Button 
              variant="danger" 
              size="sm" 
              className="position-absolute top-0 end-0"
              style={{ 
                transform: 'translate(25%, -25%)',
                zIndex: 10,
                minWidth: '24px',
                width: '24px',
                height: '24px',
                padding: '0',
                fontSize: '12px'
              }}
              onClick={() => {
                setForm({ ...form, photoOfCar: '' });
                toast.success('Car photo removed');
              }}
              title="Delete car photo"
            >
              ×
            </Button>
          </div>
        ) : '-'}
      </div>
      <div className="col-md-4 mb-3">
        <strong className="d-block text-muted mb-2">Driver ID Badge</strong>
        {(user.photoOfDriver || form.photoOfDriver) ? (
          <div className="position-relative d-inline-block">
            <img 
              src={form.photoOfDriver || user.photoOfDriver} 
              alt="Driver ID Badge" 
              style={{ 
                maxHeight: '100px', 
                maxWidth: '300px', 
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: '#f8f9fa'
              }} 
            />
            <Button 
              variant="danger" 
              size="sm" 
              className="position-absolute top-0 end-0"
              style={{ 
                transform: 'translate(25%, -25%)',
                zIndex: 10,
                minWidth: '24px',
                width: '24px',
                height: '24px',
                padding: '0',
                fontSize: '12px'
              }}
              onClick={() => {
                setForm({ ...form, photoOfDriver: '' });
                toast.success('Driver ID badge removed');
              }}
              title="Delete driver ID badge"
            >
              ×
            </Button>
          </div>
        ) : '-'}
      </div>
    </div>
  </div>

  {/* Bank Details */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-bank me-2"></i>Bank Details
    </h6>
    <div className="row">
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Account Type</strong>
        {user.accountType || '-'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Account Name</strong>
        {user.accountName || '-'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Sort Code</strong>
        {user.accountSortCode || '-'}
      </div>
      <div className="col-md-6 mb-2">
        <strong className="d-block text-muted">Account Number</strong>
        {user.accountNumber ? '****' + user.accountNumber.slice(-4) : '-'}
      </div>
    </div>
  </div>

  {/* Payment Methods */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-credit-card me-2"></i>Available Payment Methods
    </h6>
    <div className="row">
      <div className="col-12 mb-2">
        <strong className="d-block text-muted">Accepted Payment Methods</strong>
        {user.paymentMethods && user.paymentMethods.length > 0 
          ? user.paymentMethods.join(', ') 
          : 'Cash (default)'}
      </div>
    </div>
  </div>

  {/* Booking Links */}
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">
      <i className="bi bi-link-45deg me-2"></i>Booking Links
    </h6>
    <div className="row">
      <div className="col-12 mb-2">
        <p className="text-muted mb-2">
          Create unique booking links to share with customers. When customers use your links, 
          bookings will appear in your diary for you to review and accept.
        </p>
        <Button 
          variant="primary" 
          size="sm"
          onClick={() => window.location.href = '/dashboard/booking-links'}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Manage Booking Links
        </Button>
      </div>
    </div>
  </div>
</Card.Body>

        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Update Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* Basic Information Section */}
              <div className="mb-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  👤 Basic Information
                </h6>
                <div className="row">
                  <div className="col-md-12">
                    <Form.Group className="mb-2">
                      <Form.Label className="text-danger fw-bold">Name *</Form.Label>
                      <Form.Control 
                        name="name" 
                        value={form.name} 
                        onChange={handleChange}
                        className={!form.name ? 'border-danger' : ''}
                        placeholder="Required for profile completion"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label className="text-danger fw-bold">Company Name *</Form.Label>
                      <Form.Control 
                        name="companyName" 
                        value={form.companyName} 
                        onChange={handleChange}
                        className={!form.companyName ? 'border-danger' : ''}
                        placeholder="Required for profile completion"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Company Address</Form.Label>
                      <Form.Control 
                        name="companyAddress" 
                        value={form.companyAddress} 
                        onChange={handleChange}
                        placeholder="Street, Town, Postcode"
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>

              {/* Vehicle Information Section */}
              <div className="mb-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  🚗 Vehicle Information
                </h6>
                <div className="row">
                  <div className="col-md-12">

                    <Form.Group className="mb-2">
                      <Form.Label>Car Make</Form.Label>
                      <Form.Control name="carMake" placeholder="Make" value={form.carMake} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Car Model</Form.Label>
                      <Form.Control name="carModel" placeholder="Model" value={form.carModel} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Number of Seats</Form.Label>
                      <Form.Control name="carSeats" type="number" placeholder="Seats" value={form.carSeats} onChange={handleChange} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="text-danger fw-bold">Vehicle Registration Number *</Form.Label>
                      <Form.Control 
                        name="regNumber" 
                        placeholder="Required for licensing submission" 
                        value={form.regNumber} 
                        onChange={handleChange}
                        className={!form.regNumber ? 'border-danger' : ''}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Vehicle Hackney/Private ID</Form.Label>
                      <Form.Control
                        name="vehicleLicenseId"
                        placeholder="e.g., Hackney plate or PHV ID"
                        value={form.vehicleLicenseId || ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Vehicle License Expiry</Form.Label>
                      <Form.Control
                        name="vehicleLicenseExpiry"
                        type="date"
                        value={form.vehicleLicenseExpiry ? form.vehicleLicenseExpiry.split('T')[0] : ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                </div>
                
                {/* Vehicle Document Expiry Dates */}
                <div className="row mt-3">
                  <div className="col-12">
                    <h6 className="text-muted mb-3">📋 Document Expiry Dates</h6>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-2">
                      <Form.Label>Road Tax Expiry</Form.Label>
                      <Form.Control 
                        name="roadTaxExpiry" 
                        type="date"
                        value={form.roadTaxExpiry ? form.roadTaxExpiry.split('T')[0] : ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-2">
                      <Form.Label>MOT Expiry</Form.Label>
                      <Form.Control 
                        name="motExpiry" 
                        type="date"
                        value={form.motExpiry ? form.motExpiry.split('T')[0] : ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-2">
                      <Form.Label>Insurance Expiry</Form.Label>
                      <Form.Control 
                        name="insuranceExpiry" 
                        type="date"
                        value={form.insuranceExpiry ? form.insuranceExpiry.split('T')[0] : ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-2">
                      <Form.Label>Vehicle Inspection Expiry</Form.Label>
                      <Form.Control 
                        name="vehicleInspectionExpiry" 
                        type="date"
                        value={form.vehicleInspectionExpiry ? form.vehicleInspectionExpiry.split('T')[0] : ''}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-4 mt-2">
                  <Form.Label className="fw-bold">Job Card Vehicle</Form.Label>
                  <div className="d-flex flex-column flex-sm-row gap-3">
                    <Form.Check
                      type="radio"
                      id="preferredVehiclePrimary"
                      name="preferredVehicleForJobs"
                      value="primary"
                      label="Car No1 (Primary Vehicle)"
                      checked={form.preferredVehicleForJobs !== 'spare'}
                      onChange={(e) => setForm(prev => ({ ...prev, preferredVehicleForJobs: e.target.value }))}
                    />
                    <Form.Check
                      type="radio"
                      id="preferredVehicleSpare"
                      name="preferredVehicleForJobs"
                      value="spare"
                      label="Car No2 (Spare Vehicle)"
                      checked={form.preferredVehicleForJobs === 'spare'}
                      disabled={!formSpareVehicleHasData}
                      onChange={(e) => setForm(prev => ({ ...prev, preferredVehicleForJobs: e.target.value }))}
                    />
                  </div>
                  <Form.Text className="text-muted">Car No1 is default.</Form.Text>
                </Form.Group>

                {/* Spare Vehicle Section */}
                <div className="mt-4">
                  <h6 className="text-primary border-bottom pb-2 mb-3">
                    🚗 Spare / Second Vehicle (Optional)
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Car Make</Form.Label>
                        <Form.Control
                          value={form.spareVehicle?.carMake || ''}
                          onChange={(e) => handleSpareVehicleChange('carMake', e.target.value)}
                          placeholder="e.g., Mercedes"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Car Model</Form.Label>
                        <Form.Control
                          value={form.spareVehicle?.carModel || ''}
                          onChange={(e) => handleSpareVehicleChange('carModel', e.target.value)}
                          placeholder="e.g., E-Class"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Number of Seats</Form.Label>
                        <Form.Control
                          type="number"
                          value={form.spareVehicle?.carSeats || ''}
                          onChange={(e) => handleSpareVehicleChange('carSeats', e.target.value)}
                          placeholder="e.g., 4"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Registration Number</Form.Label>
                        <Form.Control
                          value={form.spareVehicle?.regNumber || ''}
                          onChange={(e) => handleSpareVehicleChange('regNumber', e.target.value)}
                          placeholder="e.g., AB12 CDE"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Vehicle Hackney/Private ID</Form.Label>
                        <Form.Control
                          value={form.spareVehicle?.vehicleLicenseId || ''}
                          onChange={(e) => handleSpareVehicleChange('vehicleLicenseId', e.target.value)}
                          placeholder="e.g., Hackney plate number"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Vehicle License Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.spareVehicle?.vehicleLicenseExpiry ? form.spareVehicle.vehicleLicenseExpiry.split('T')[0] : ''}
                          onChange={(e) => handleSpareVehicleChange('vehicleLicenseExpiry', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-12">
                      <h6 className="text-muted mb-3">📋 Spare Vehicle Document Expiry Dates</h6>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Road Tax Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.spareVehicle?.roadTaxExpiry ? form.spareVehicle.roadTaxExpiry.split('T')[0] : ''}
                          onChange={(e) => handleSpareVehicleChange('roadTaxExpiry', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>MOT Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.spareVehicle?.motExpiry ? form.spareVehicle.motExpiry.split('T')[0] : ''}
                          onChange={(e) => handleSpareVehicleChange('motExpiry', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Insurance Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.spareVehicle?.insuranceExpiry ? form.spareVehicle.insuranceExpiry.split('T')[0] : ''}
                          onChange={(e) => handleSpareVehicleChange('insuranceExpiry', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-2">
                        <Form.Label>Vehicle Inspection Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          value={form.spareVehicle?.vehicleInspectionExpiry ? form.spareVehicle.vehicleInspectionExpiry.split('T')[0] : ''}
                          onChange={(e) => handleSpareVehicleChange('vehicleInspectionExpiry', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Photo / Reference URL</Form.Label>
                    <Form.Control
                      value={form.spareVehicle?.photoOfCar || ''}
                      onChange={(e) => handleSpareVehicleChange('photoOfCar', e.target.value)}
                      placeholder="https://example.com/spare-car.jpg"
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Email Preferences Section */}
              <div className="mb-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  📧 Email Preferences
                </h6>
                <div className="row">
                  <div className="col-md-12">
                    <Form.Group className="mb-2">
                      <Form.Check 
                        type="checkbox"
                        id="leadEmailOptIn"
                        name="leadEmailOptIn"
                        label="Receive new job leads by email"
                        checked={!!form.leadEmailOptIn}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Check 
                        type="checkbox"
                        id="emailDiaryDaily"
                        name="emailDiaryDaily"
                        label="Email me my Diary each morning at 6am"
                        checked={!!form.emailDiaryDaily}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Check 
                        type="checkbox"
                        id="audioNotifications"
                        name="audioNotifications"
                        label="Enable audio notifications for new jobs and system alerts"
                        checked={!!form.audioNotifications}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Check 
                        type="checkbox"
                        id="webLinksToDiary"
                        name="webLinksToDiary"
                        label="Insert WebLink jobs directly into My Diary (like MyLink jobs)"
                        checked={!!form.webLinksToDiary}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" name="email" value={form.email} onChange={handleChange} />
                    </Form.Group>
                  </div>
                </div>
              </div>

          {/* Dispatch Information Section */}
          <div className="mb-4">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              🚛 Dispatch Information
            </h6>
            <div className="row">
              <div className="col-md-12">
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox"
                    id="dispatchAccessAccepted"
                    name="dispatchAccessAccepted"
                    label="Allow Dispatch Module access to my diary and job assignments"
                    checked={!!form.dispatchAccessAccepted}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    By checking this, you allow dispatch personnel to view your diary and assign jobs to you directly.
                  </Form.Text>
                </Form.Group>

                {form.dispatchAccessAccepted && (
                  <Form.Group className="mb-3">
                    <Form.Label>Dispatcher Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      name="dispatchEmail" 
                      value={form.dispatchEmail} 
                      onChange={handleChange}
                      placeholder="e.g., dispatchdemo@taxi-local.com"
                      isInvalid={form.dispatchEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.dispatchEmail)}
                    />
                    <Form.Text className="text-muted">
                      Enter your assigned dispatcher's email address to enable direct communication with the dispatch module.
                    </Form.Text>
                    {form.dispatchEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.dispatchEmail) && (
                      <Form.Control.Feedback type="invalid">
                        Please enter a valid email address
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="mb-4">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              📞 Contact Information
            </h6>
            <div className="row">
              <div className="col-md-12">

              <Form.Group className="mb-2">
                <Form.Label className="text-danger fw-bold">Phone Number *</Form.Label>
                <Form.Control 
                  name="phoneNumber" 
                  value={form.phoneNumber} 
                  onChange={handleChange}
                  className={!form.phoneNumber ? 'border-danger' : ''}
                  placeholder="Required for profile completion"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Website</Form.Label>
                <Form.Control name="website" value={form.website} onChange={handleChange} />
              </Form.Group>
              </div>
            </div>
          </div>

          {/* Licensing Information Section */}
          <div className="mb-4">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              📋 Licensing Information (Required for Job Acceptance)
            </h6>
            <div className="row">
              <div className="col-md-12">
              <Form.Group className="mb-2">
                <Form.Label className="text-danger fw-bold">Licensing Authority *</Form.Label>
                {licensingBodies.length > 0 ? (
                  <Form.Select
                    name="licensingAuthority" 
                    value={form.licensingAuthority || ''} 
                    onChange={handleChange}
                    className={!form.licensingAuthority ? 'border-danger' : ''}
                  >
                    <option value="">Select Licensing Authority...</option>
                    {licensingBodies.map((body) => (
                      <option key={body._id} value={body.code}>
                        {body.name} ({body.code})
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control 
                    name="licensingAuthority" 
                    value={form.licensingAuthority || ''} 
                    onChange={handleChange}
                    className={!form.licensingAuthority ? 'border-danger' : ''}
                    placeholder="Required for licensing submission (e.g., EDDC, DDC)"
                  />
                )}
                <Form.Text className="text-muted">
                  {licensingBodies.length > 0 
                    ? 'Select your licensing authority from System Administration > Fare Tariffs'
                    : 'Enter your licensing authority code (e.g., EDDC, DDC) to match with Fare Tariffs'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="text-danger fw-bold">Badge Number *</Form.Label>
                <Form.Control 
                  name="badgeNumber" 
                  value={form.badgeNumber} 
                  onChange={handleChange}
                  className={!form.badgeNumber ? 'border-danger' : ''}
                  placeholder="Required for licensing submission"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="text-danger fw-bold">License Type *</Form.Label>
                <Form.Select 
                  name="licenseType" 
                  value={form.licenseType} 
                  onChange={handleChange}
                  className={!form.licenseType ? 'border-danger' : ''}
                >
                  <option value="">Select license type (Required)</option>
                  <option value="Hackney License">Hackney License</option>
                  <option value="Private Hire">Private Hire</option>
                </Form.Select>
              </Form.Group>
              </div>
            </div>
          </div>

          {/* Driver Profile Section */}
          <div className="mb-4">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              👤 Driver Profile
            </h6>
            <div className="row">
              <div className="col-md-12">
              <Form.Group className="mb-2">
                <Form.Label>Logo</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control 
                    name="logo" 
                    placeholder="https://example.com/logo.png" 
                    value={form.logo} 
                    onChange={handleChange} 
                  />
                  {/* Choose from files */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 400;
                          const maxHeight = 300;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
                          setForm({ ...form, logo: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="logoUpload"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('logoUpload').click()}
                    title="Upload logo from files"
                  >
                    📁
                  </Button>
                  {/* Take photo */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 400;
                          const maxHeight = 300;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
                          setForm({ ...form, logo: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="logoCapture"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('logoCapture').click()}
                    title="Take logo photo with camera"
                  >
                    📷
                  </Button>
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Car Photo</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control 
                    name="photoOfCar" 
                    placeholder="https://example.com/car-photo.jpg" 
                    value={form.photoOfCar} 
                    onChange={handleChange} 
                  />
                  {/* Choose car photo from files */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 800; const maxHeight = 600;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                          setForm({ ...form, photoOfCar: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="carPhotoFile"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('carPhotoFile').click()}
                    title="Choose car photo from files"
                  >
                    📁
                  </Button>
                  {/* Take car photo */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 800; const maxHeight = 600;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                          setForm({ ...form, photoOfCar: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="carPhotoUpload"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('carPhotoUpload').click()}
                    title="Take car photo with camera"
                  >
                    📷
                  </Button>
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Driver ID Badge</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control 
                    name="photoOfDriver" 
                    placeholder="https://example.com/driver-id-badge.jpg" 
                    value={form.photoOfDriver} 
                    onChange={handleChange} 
                  />
                  {/* Choose driver badge photo from files */}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 800; const maxHeight = 600;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                          setForm({ ...form, photoOfDriver: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="driverPhotoFile"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('driverPhotoFile').click()}
                    title="Choose driver ID badge from files"
                  >
                    📁
                  </Button>
                  {/* Take driver badge photo */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const maxWidth = 800; const maxHeight = 600;
                          let { width, height } = img;
                          if (width > height) {
                            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
                          } else {
                            if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
                          }
                          canvas.width = width; canvas.height = height;
                          ctx.drawImage(img, 0, 0, width, height);
                          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                          setForm({ ...form, photoOfDriver: compressedDataUrl });
                        };
                        img.src = URL.createObjectURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="driverPhotoUpload"
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => document.getElementById('driverPhotoUpload').click()}
                    title="Take driver ID badge photo with camera"
                  >
                    📷
                  </Button>
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Main Postcode</Form.Label>
                <Form.Control name="mainPostcode" placeholder="EX12" value={form.mainPostcode} onChange={handleChange} />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Postcode Sectors Covered (space separated)</Form.Label>
                <Form.Control 
                  name="postcodeSectorsCovered" 
                  placeholder="EX12 EX13 DT6" 
                  value={Array.isArray(form.postcodeSectorsCovered) ? form.postcodeSectorsCovered.join(' ') : form.postcodeSectorsCovered} 
                  onChange={(e) => {
                    const sectors = e.target.value.split(' ').map(s => s.trim()).filter(s => s);
                    setForm({ ...form, postcodeSectorsCovered: sectors });
                  }} 
                />
              </Form.Group>
              </div>
            </div>
          </div>

          {/* Bank Details Section */}
          <div className="mb-4">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              🏦 Bank Details
            </h6>
            <div className="row">
              <div className="col-md-12">
              <Form.Group className="mb-2">
                <Form.Label>Account Type</Form.Label>
                <Form.Select name="accountType" value={form.accountType} onChange={handleChange}>
                  <option value="Personal">Personal</option>
                  <option value="Business">Business</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Account Name</Form.Label>
                <Form.Control name="accountName" placeholder="Account holder name" value={form.accountName} onChange={handleChange} />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Sort Code</Form.Label>
                <Form.Control name="accountSortCode" placeholder="12-34-56" value={form.accountSortCode} onChange={handleChange} />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Account Number</Form.Label>
                <Form.Control name="accountNumber" placeholder="12345678" value={form.accountNumber} onChange={handleChange} />
              </Form.Group>

              <Form.Label className="mt-3">Available Payment Methods</Form.Label>
              <div className="d-flex flex-column align-items-start gap-1 mb-3">
                <Form.Check
                  type="checkbox"
                  id="payment-cash"
                  label="Cash"
                  checked={form.paymentMethods && form.paymentMethods.includes('Cash')}
                  onChange={() => handlePaymentMethodChange('Cash')}
                />
                <Form.Check
                  type="checkbox"
                  id="payment-card"
                  label="Card"
                  checked={form.paymentMethods && form.paymentMethods.includes('Card')}
                  onChange={() => handlePaymentMethodChange('Card')}
                />
                <Form.Check
                  type="checkbox"
                  id="payment-bank-transfer"
                  label="Bank Transfer"
                  checked={form.paymentMethods && form.paymentMethods.includes('BankTransfer')}
                  onChange={() => handlePaymentMethodChange('BankTransfer')}
                />
                <Form.Check
                  type="checkbox"
                  id="payment-invoiced"
                  label="Invoiced"
                  checked={form.paymentMethods && form.paymentMethods.includes('Invoiced')}
                  onChange={() => handlePaymentMethodChange('Invoiced')}
                />
              </div>
              </div>
            </div>
          </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" variant="primary">Save Changes</Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Licensing Submission Button */}
        {profileStatus && profileStatus.profileCompleted && !profileStatus.licensingSubmitted && (
          <Card className="mb-3">
            <Card.Body className="bg-warning bg-opacity-10 border-warning">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-warning mb-1">
                    <i className="bi bi-exclamation-triangle me-2"></i>Licensing Information Required
                  </h6>
                  <p className="mb-0 text-muted">
                    Your profile is complete, but you need to submit licensing information to accept jobs.
                  </p>
                </div>
                <Button 
                  variant="warning" 
                  onClick={handleLicensingSubmission}
                  disabled={!form.licensingAuthority || !form.badgeNumber || !form.licenseType || !form.regNumber}
                >
                  Submit Licensing Information
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Licensing Status */}
        {profileStatus && profileStatus.licensingSubmitted && !profileStatus.licensingApproved && (
          <Card className="mb-3">
            <Card.Body className="bg-info bg-opacity-10 border-info">
              <div className="d-flex align-items-center">
                <i className="bi bi-clock-history me-2 text-info"></i>
                <div>
                  <h6 className="text-info mb-1">Licensing Under Review</h6>
                  <p className="mb-0 text-muted">
                    Your licensing information has been submitted and is being reviewed by our admin team.
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </>
  );
}
